import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';

const ManualUserList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'manual'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedData, setUploadedData] = useState([]);
  const [manualUsers, setManualUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    name: '',
    category: '',
    address_full: '',
    address_1: '',
    address_2: '',
    phone_number: '',
    website: '',
    rating: '',
    reviews_count: '',
    image_source: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Helper function to check if number is a mobile number (not STD/landline)
  const isMobileNumber = (phoneNumber) => {
    if (!phoneNumber) return false;
    
    // Remove all spaces and special characters except + and digits
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // If it starts with +, remove it for checking
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    
    // Remove country code if present (91)
    if (cleaned.startsWith('91') && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    }
    
    // Check if it's a 10-digit number
    if (cleaned.length !== 10) {
      return false;
    }
    
    // Indian mobile numbers start with 6, 7, 8, or 9
    const firstDigit = parseInt(cleaned.charAt(0));
    if (firstDigit >= 6 && firstDigit <= 9) {
      return true;
    }
    
    // Check for specific STD codes that are commonly mistaken for mobile
    const stdCodes = [
      '11', '22', '33', '44', '20', '40', '79', '80', '120', '124', '129', '141', '144', '151', '161', '172', '181', '191', '194', '212', '214', '222', '231', '240', '251', '261', '271', '281', '291', '297', '298', '299', '300', '301', '302', '303', '304', '305', '306', '307', '308', '309', '310', '311', '312', '313', '314', '315', '316', '317', '318', '319', '320', '321', '322', '323', '324', '325', '326', '327', '328', '329', '330', '331', '332', '333', '334', '335', '336', '337', '338', '339', '340', '341', '342', '343', '344', '345', '346', '347', '348', '349', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '360', '361', '362', '363', '364', '365', '366', '367', '368', '369', '370', '371', '372', '373', '374', '375', '376', '377', '378', '379', '380', '381', '382', '383', '384', '385', '386', '387', '388', '389', '390', '391', '392', '393', '394', '395', '396', '397', '398', '399', '400', '401', '402', '403', '404', '405', '406', '407', '408', '409', '410', '411', '412', '413', '414', '415', '416', '417', '418', '419', '420', '421', '422', '423', '424', '425', '426', '427', '428', '429', '430', '431', '432', '433', '434', '435', '436', '437', '438', '439', '440', '441', '442', '443', '444', '445', '446', '447', '448', '449', '450', '451', '452', '453', '454', '455', '456', '457', '458', '459', '460', '461', '462', '463', '464', '465', '466', '467', '468', '469', '470', '471', '472', '473', '474', '475', '476', '477', '478', '479', '480', '481', '482', '483', '484', '485', '486', '487', '488', '489', '490', '491', '492', '493', '494', '495', '496', '497', '498', '499', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '510', '511', '512', '513', '514', '515', '516', '517', '518', '519', '520', '521', '522', '523', '524', '525', '526', '527', '528', '529', '530', '531', '532', '533', '534', '535', '536', '537', '538', '539', '540', '541', '542', '543', '544', '545', '546', '547', '548', '549', '550', '551', '552', '553', '554', '555', '556', '557', '558', '559', '560', '561', '562', '563', '564', '565', '566', '567', '568', '569', '570', '571', '572', '573', '574', '575', '576', '577', '578', '579', '580', '581', '582', '583', '584', '585', '586', '587', '588', '589', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '600', '601', '602', '603', '604', '605', '606', '607', '608', '609', '610', '611', '612', '613', '614', '615', '616', '617', '618', '619', '620', '621', '622', '623', '624', '625', '626', '627', '628', '629', '630', '631', '632', '633', '634', '635', '636', '637', '638', '639', '640', '641', '642', '643', '644', '645', '646', '647', '648', '649', '650', '651', '652', '653', '654', '655', '656', '657', '658', '659', '660', '661', '662', '663', '664', '665', '666', '667', '668', '669', '670', '671', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692', '693', '694', '695', '696', '697', '698', '699', '700', '701', '702', '703', '704', '705', '706', '707', '708', '709', '710', '711', '712', '713', '714', '715', '716', '717', '718', '719', '720', '721', '722', '723', '724', '725', '726', '727', '728', '729', '730', '731', '732', '733', '734', '735', '736', '737', '738', '739', '740', '741', '742', '743', '744', '745', '746', '747', '748', '749', '750', '751', '752', '753', '754', '755', '756', '757', '758', '759', '760', '761', '762', '763', '764', '765', '766', '767', '768', '769', '770', '771', '772', '773', '774', '775', '776', '777', '778', '779', '780', '781', '782', '783', '784', '785', '786', '787', '788', '789', '790', '791', '792', '793', '794', '795', '796', '797', '798', '799', '800', '801', '802', '803', '804', '805', '806', '807', '808', '809', '810', '811', '812', '813', '814', '815', '816', '817', '818', '819', '820', '821', '822', '823', '824', '825', '826', '827', '828', '829', '830', '831', '832', '833', '834', '835', '836', '837', '838', '839', '840', '841', '842', '843', '844', '845', '846', '847', '848', '849', '850', '851', '852', '853', '854', '855', '856', '857', '858', '859', '860', '861', '862', '863', '864', '865', '866', '867', '868', '869', '870', '871', '872', '873', '874', '875', '876', '877', '878', '879', '880', '881', '882', '883', '884', '885', '886', '887', '888', '889', '890', '891', '892', '893', '894', '895', '896', '897', '898', '899', '900', '901', '902', '903', '904', '905', '906', '907', '908', '909', '910', '911', '912', '913', '914', '915', '916', '917', '918', '919', '920', '921', '922', '923', '924', '925', '926', '927', '928', '929', '930', '931', '932', '933', '934', '935', '936', '937', '938', '939', '940', '941', '942', '943', '944', '945', '946', '947', '948', '949', '950', '951', '952', '953', '954', '955', '956', '957', '958', '959', '960', '961', '962', '963', '964', '965', '966', '967', '968', '969', '970', '971', '972', '973', '974', '975', '976', '977', '978', '979', '980', '981', '982', '983', '984', '985', '986', '987', '988', '989', '990', '991', '992', '993', '994', '995', '996', '997', '998', '999'
    ];
    
    // Check if the number starts with any STD code
    for (let i = 0; i < stdCodes.length; i++) {
      if (cleaned.startsWith(stdCodes[i])) {
        return false; // It's a landline number
      }
    }
    
    return true; // It's a mobile number
  };

  // Helper function to format phone number
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all spaces and special characters except + and digits
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // If it doesn't start with +, add +91
    if (!cleaned.startsWith('+')) {
      // If it starts with 91 and has 12 digits, add +
      if (cleaned.startsWith('91') && cleaned.length === 12) {
        cleaned = '+' + cleaned;
      }
      // If it's a 10-digit number, add +91
      else if (cleaned.length === 10) {
        cleaned = '+91' + cleaned;
      }
      // If it's already 12 digits without country code, add +91
      else if (cleaned.length === 12) {
        cleaned = '+91' + cleaned;
      }
    }
    
    return cleaned;
  };

  // Helper function to parse address and extract location components
  const parseAddress = (addressFull) => {
    if (!addressFull || typeof addressFull !== 'string') {
      return {
        locality: '',
        city: '',
        state: '',
        pinCode: '',
        country: ''
      };
    }

    // Remove extra spaces and trim
    let address = addressFull.trim().replace(/\s+/g, ' ');
    
    // Split by commas
    const parts = address.split(',').map(part => part.trim()).filter(part => part.length > 0);
    
    if (parts.length === 0) {
      return {
        locality: '',
        city: '',
        state: '',
        pinCode: '',
        country: ''
      };
    }

    // Initialize result object
    const result = {
      locality: '',
      city: '',
      state: '',
      pinCode: '',
      country: ''
    };

    // Extract from right to left (end to beginning) as per the format
    let currentIndex = parts.length - 1;

    // Extract Country (last part - rightmost)
    if (currentIndex >= 0) {
      result.country = parts[currentIndex];
      currentIndex--;
    }

    // Extract PinCode and State (second to last part)
    if (currentIndex >= 0) {
      const pinCodePart = parts[currentIndex];
      // Check if this part contains a 6-digit pin code
      const pinCodeMatch = pinCodePart.match(/\b\d{6}\b/);
      if (pinCodeMatch) {
        result.pinCode = pinCodeMatch[0];
        // Remove pin code from the part and use remaining as state
        const statePart = pinCodePart.replace(/\b\d{6}\b/, '').trim();
        if (statePart) {
          result.state = statePart;
        }
      } else {
        // If no pin code found, assume this is state
        result.state = pinCodePart;
      }
      currentIndex--;
    }

    // Extract City (third to last part)
    if (currentIndex >= 0) {
      result.city = parts[currentIndex];
      currentIndex--;
    }

    // Extract Locality (fourth to last part - just the locality, not everything)
    if (currentIndex >= 0) {
      result.locality = parts[currentIndex];
      currentIndex--;
    }

    return result;
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      console.log('File uploaded:', file.name);
      
      try {
        // Read the Excel file
        const data = await readExcelFile(file);
        console.log('Parsed Excel data:', data);
        
        // Process and format phone numbers
        const processedData = data.map(record => ({
          ...record,
          phone_number: formatPhoneNumber(record.phone_number || ''),
          originalPhone: record.phone_number || '', // Keep original for reference
          address_full: record.address_full || '', // Keep original for reference
          address_1: record.address_1 || '', // Keep original for reference
          address_2: record.address_2 || '', // Keep original for reference
          locality: parseAddress(record.address_full).locality,
          city: parseAddress(record.address_full).city,
          state: parseAddress(record.address_full).state,
          pinCode: parseAddress(record.address_full).pinCode,
          country: parseAddress(record.address_full).country
        }));
        
        // Filter out records without phone numbers and non-mobile numbers
        const validRecords = processedData.filter(record => {
          const hasPhone = record.phone_number && record.phone_number.trim() !== '';
          const isMobile = isMobileNumber(record.originalPhone);
          return hasPhone && isMobile;
        });
        
        // Get rejected records for reporting
        const rejectedRecords = processedData.filter(record => {
          const hasPhone = record.phone_number && record.phone_number.trim() !== '';
          const isMobile = isMobileNumber(record.originalPhone);
          return hasPhone && !isMobile;
        });
        
        setUploadedData(validRecords);
        
        console.log('Original data:', data);
        console.log('Processed data with formatted phone numbers:', processedData);
        console.log('Valid mobile records for WhatsApp:', validRecords);
        console.log('Rejected landline/STD numbers:', rejectedRecords);
        console.log('Records without phone numbers:', 
          data.length - processedData.filter(r => r.phone_number && r.phone_number.trim() !== '').length);
        console.log('Total rejected (landline + no phone):', 
          data.length - validRecords.length);
        
        // Save to Firebase
        await saveToFirebase(validRecords, rejectedRecords.length);
        
      } catch (error) {
        console.error('Error processing file:', error);
        alert('Error processing file: ' + error.message);
      }
    }
  };

  // Function to read Excel file
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          // Get headers from first row
          const headers = jsonData[0];
          console.log('Excel headers:', headers);
          
          // Convert rows to objects
          const records = jsonData.slice(1).map(row => {
            const record = {};
            headers.forEach((header, index) => {
              if (header && row[index] !== undefined) {
                record[header] = row[index];
              }
            });
            return record;
          });
          
          resolve(records);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  // Function to save data to Firebase
  const saveToFirebase = async (records, rejectedCount = 0) => {
    try {
      const db = getFirestore();
      const collectionRef = collection(db, 'ManualUserList');
      
      const savePromises = records.map(record => {
        // Parse address and add location components
        const addressComponents = parseAddress(record.address_full);
        
        const businessData = {
          ...record,
          ...addressComponents, // Add locality, city, state, pinCode, country
          sendReferral: false, // Default value
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Remove originalPhone field as it's not needed in Firebase
        delete businessData.originalPhone;
        
        return addDoc(collectionRef, businessData);
      });
      
      await Promise.all(savePromises);
      console.log(`Successfully saved ${records.length} records to Firebase`);
      
      let message = `Successfully uploaded ${records.length} businesses to Firebase!`;
      if (rejectedCount > 0) {
        message += `\n\n⚠️ ${rejectedCount} landline/STD numbers were filtered out (cannot receive WhatsApp messages).`;
      }
      
      alert(message);
      
    } catch (error) {
      console.error('Error saving to Firebase:', error);
      alert('Error saving to Firebase: ' + error.message);
    }
  };

  // Handle manual user addition
  const handleAddUser = async () => {
    if (newUser.name && newUser.phone_number) {
      // Check if it's a mobile number
      if (!isMobileNumber(newUser.phone_number)) {
        alert('⚠️ This appears to be a landline/STD number. Only mobile numbers can receive WhatsApp messages.');
        return;
      }
      
      try {
        const db = getFirestore();
        const collectionRef = collection(db, 'ManualUserList');
        
        // Format the phone number before adding
        const formattedUser = {
          ...newUser,
          phone_number: formatPhoneNumber(newUser.phone_number),
          id: Date.now()
        };
        
        // Parse address and add location components
        const addressComponents = parseAddress(newUser.address_full);
        const businessData = {
          ...formattedUser,
          ...addressComponents, // Add locality, city, state, pinCode, country
          sendReferral: false, // Default value
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Remove the id field as Firebase will generate its own
        delete businessData.id;
        
        const docRef = await addDoc(collectionRef, businessData);
        console.log('Business saved to Firebase with ID:', docRef.id);
        
        // Add to local state with Firebase ID
        const userWithFirebaseId = {
          ...formattedUser,
          firebaseId: docRef.id
        };
        
        setManualUsers([...manualUsers, userWithFirebaseId]);
        setNewUser({
          name: '',
          category: '',
          address_full: '',
          address_1: '',
          address_2: '',
          phone_number: '',
          website: '',
          rating: '',
          reviews_count: '',
          image_source: ''
        });
        
        alert('Business added successfully and saved to Firebase!');
        
      } catch (error) {
        console.error('Error saving to Firebase:', error);
        alert('Error saving to Firebase: ' + error.message);
      }
    }
  };

  // Handle manual user removal
  const handleRemoveUser = (userId) => {
    setManualUsers(manualUsers.filter(user => user.id !== userId));
  };

  // Handle sending referrals
  const handleSendReferrals = async () => {
    setIsProcessing(true);
    try {
      // TODO: Implement the actual referral sending logic
      console.log('Sending referrals for:', {
        uploadedFile: uploadedFile?.name,
        manualUsers: manualUsers
      });
      
      // Simulate processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert('Referrals sent successfully!');
    } catch (error) {
      console.error('Error sending referrals:', error);
      alert('Error sending referrals. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const totalUsers = manualUsers.length + uploadedData.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">Upload Businesses</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {totalUsers} businesses ready for referral
              </div>
              <button
                onClick={() => navigate('/manual-business-list')}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                View All Businesses
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-6">
        {/* Action Button */}
        {totalUsers > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-blue-900">
                  Send WhatsApp Referrals
                </h3>
                <p className="text-sm text-blue-700">
                  {totalUsers} business(es) ready to receive referral messages
                </p>
              </div>
              <button
                onClick={handleSendReferrals}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>Send Referral Via WhatsApp</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('upload')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'upload'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <DocumentArrowUpIcon className="w-5 h-5 inline mr-2" />
                Upload Excel File
              </button>
              <button
                onClick={() => setActiveTab('manual')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'manual'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <PlusIcon className="w-5 h-5 inline mr-2" />
                Manual Entry
              </button>
            </nav>
          </div>

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="mt-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Excel File
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                        >
                          <span>Upload a file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        Excel files (.xlsx, .xls) or CSV files
                      </p>
                    </div>
                  </div>
                </div>

                {uploadedFile && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex items-center">
                      <CheckIcon className="w-5 h-5 text-green-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          File uploaded successfully
                        </p>
                        <p className="text-sm text-green-700">
                          {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                        </p>
                        {uploadedData.length > 0 && (
                          <p className="text-sm text-green-700">
                            {uploadedData.length} businesses processed and saved to Firebase
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setUploadedFile(null);
                          setUploadedData([]);
                        }}
                        className="ml-auto text-green-400 hover:text-green-600"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Display uploaded data */}
                {uploadedData.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <h4 className="text-sm font-medium text-gray-800">
                        Uploaded Businesses ({uploadedData.length})
                      </h4>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Category
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Phone Number
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Rating
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {uploadedData.map((business, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                {business.name}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {business.category || '-'}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {business.phone_number}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                {business.rating ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    ⭐ {business.rating}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Excel File Format</h4>
                  <p className="text-sm text-blue-700 mb-2">
                    Your Excel file should contain the following columns:
                  </p>
                  <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                    <li><strong>name:</strong> Business/User name</li>
                    <li><strong>category:</strong> Business category (e.g., Parking lot, Restaurant)</li>
                    <li><strong>address_full:</strong> Complete address</li>
                    <li><strong>address_1:</strong> Address line 1</li>
                    <li><strong>address_2:</strong> Address line 2</li>
                    <li><strong>phone_number:</strong> Mobile phone number (required for WhatsApp - only mobile numbers starting with 6, 7, 8, or 9 are accepted)</li>
                    <li><strong>website:</strong> Website URL (optional)</li>
                    <li><strong>rating:</strong> Rating value (optional)</li>
                    <li><strong>reviews_count:</strong> Number of reviews (optional)</li>
                    <li><strong>image_source:</strong> Image URL (optional)</li>
                  </ul>
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-800 font-medium">Phone Number Formatting:</p>
                    <p className="text-sm text-green-700">
                      Phone numbers will be automatically formatted to include +91 country code and remove spaces.
                      Examples: "98903 56050" → "+919890356050", "9890356051" → "+919890356051"
                    </p>
                  </div>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800 font-medium">Important:</p>
                    <p className="text-sm text-yellow-700">
                      Only mobile numbers (starting with 6, 7, 8, or 9) will be processed for WhatsApp referrals. 
                      Landline/STD numbers will be automatically filtered out as they cannot receive WhatsApp messages.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Manual Entry Tab */}
          {activeTab === 'manual' && (
            <div className="mt-6">
              <div className="space-y-4">
                {/* Add New User Form */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-800 mb-3">Add New Business/User</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter business name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <input
                        type="text"
                        value={newUser.category}
                        onChange={(e) => setNewUser({ ...newUser, category: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Parking lot, Restaurant"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        value={newUser.phone_number}
                        onChange={(e) => setNewUser({ ...newUser, phone_number: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 9890356050 (mobile only)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 1
                      </label>
                      <input
                        type="text"
                        value={newUser.address_1}
                        onChange={(e) => setNewUser({ ...newUser, address_1: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Address line 1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        value={newUser.address_2}
                        onChange={(e) => setNewUser({ ...newUser, address_2: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Address line 2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                      </label>
                      <input
                        type="url"
                        value={newUser.website}
                        onChange={(e) => setNewUser({ ...newUser, website: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rating
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={newUser.rating}
                        onChange={(e) => setNewUser({ ...newUser, rating: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="4.5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reviews Count
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={newUser.reviews_count}
                        onChange={(e) => setNewUser({ ...newUser, reviews_count: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="25"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleAddUser}
                        disabled={!newUser.name || !newUser.phone_number}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>Add Business</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Manual Users List */}
                {manualUsers.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-800 mb-3">
                      Manual Businesses ({manualUsers.length})
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Category
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Phone Number
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Address
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Rating
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {manualUsers.map((user) => (
                            <tr key={user.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {user.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {user.category || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {user.phone_number}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                <div className="max-w-xs">
                                  {user.address_1 && <div>{user.address_1}</div>}
                                  {user.address_2 && <div className="text-gray-500">{user.address_2}</div>}
                                  {!user.address_1 && !user.address_2 && <span className="text-gray-400">-</span>}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {user.rating ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    ⭐ {user.rating}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <button
                                  onClick={() => handleRemoveUser(user.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualUserList;
